import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Mail, Send, Pencil, Trash2, Paperclip, RefreshCw, Eye, EyeOff, Plus, Tag } from "lucide-react";
import NotifyEmailField from "@/components/NotifyEmailField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminNewsletterTab() {
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [topics, setTopics] = useState([]);
  const [view, setView] = useState("subscribers");
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [topicDialog, setTopicDialog] = useState(null);
  const [topicForm, setTopicForm] = useState({ key_name: "", label_de: "", label_en: "", description_de: "", description_en: "", visible: 1, sort_order: 10 });
  const [sendForm, setSendForm] = useState({ subject: "", body: "", topics: [], attachment: null });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [resending, setResending] = useState(null);

  const fetchSubscribers = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/newsletter/subscribers`, { withCredentials: true }); setSubscribers(data); } catch (err) { console.error(err); }
  }, []);
  const fetchCampaigns = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/newsletter/campaigns`, { withCredentials: true }); setCampaigns(data); } catch (err) { console.error(err); }
  }, []);
  const fetchTopics = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/newsletter/topics/all`, { withCredentials: true }); setTopics(data); } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchSubscribers(); fetchCampaigns(); fetchTopics(); }, [fetchSubscribers, fetchCampaigns, fetchTopics]);

  const openEdit = (sub) => { setEditForm({ ...sub }); setEditDialog(sub); };
  const saveEdit = async () => { await axios.put(`${API}/newsletter/subscribers/${editDialog.id}`, editForm, { withCredentials: true }); setEditDialog(null); fetchSubscribers(); };
  const deleteSub = async (id) => { if (!window.confirm("Abonnent wirklich löschen?")) return; await axios.delete(`${API}/newsletter/subscribers/${id}`, { withCredentials: true }); fetchSubscribers(); };

  const resendConfirm = async (id) => {
    setResending(id);
    try { const { data } = await axios.post(`${API}/newsletter/subscribers/${id}/resend`, {}, { withCredentials: true }); alert(data.message); fetchSubscribers(); }
    catch (err) { alert("Fehler beim Senden"); }
    finally { setResending(null); }
  };

  // Topics CRUD
  const openTopicCreate = () => { setTopicDialog("new"); setTopicForm({ key_name: "", label_de: "", label_en: "", description_de: "", description_en: "", visible: 1, sort_order: topics.length + 1 }); };
  const openTopicEdit = (t) => { setTopicDialog(t); setTopicForm({ ...t }); };
  const saveTopic = async () => {
    if (topicDialog === "new") {
      await axios.post(`${API}/newsletter/topics`, topicForm, { withCredentials: true });
    } else {
      await axios.put(`${API}/newsletter/topics/${topicDialog.id}`, topicForm, { withCredentials: true });
    }
    setTopicDialog(null); fetchTopics();
  };
  const deleteTopic = async (id) => { if (!window.confirm("Thema löschen?")) return; await axios.delete(`${API}/newsletter/topics/${id}`, { withCredentials: true }); fetchTopics(); };
  const toggleTopicVisible = async (topic) => {
    await axios.put(`${API}/newsletter/topics/${topic.id}`, { visible: topic.visible ? 0 : 1 }, { withCredentials: true }); fetchTopics();
  };

  const handleSend = async () => {
    setSending(true); setSendResult(null);
    try {
      const fd = new FormData(); fd.append("subject", sendForm.subject); fd.append("body", sendForm.body); fd.append("topics", JSON.stringify(sendForm.topics));
      if (sendForm.attachment) fd.append("attachment", sendForm.attachment);
      const { data } = await axios.post(`${API}/newsletter/send`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setSendResult(data.message); fetchCampaigns();
    } catch { setSendResult("Fehler beim Versenden"); }
    finally { setSending(false); }
  };

  const toggleSendTopic = (t) => setSendForm(prev => ({ ...prev, topics: prev.topics.includes(t) ? prev.topics.filter(x => x !== t) : [...prev.topics, t] }));

  const confirmed = subscribers.filter(s => s.status === "confirmed").length;
  const pending = subscribers.filter(s => s.status === "pending").length;
  const statusLabel = { confirmed: "Bestätigt", pending: "Ausstehend", unsubscribed: "Abgemeldet" };
  const statusColor = { confirmed: "text-green-400", pending: "text-yellow-400", unsubscribed: "text-red-400" };

  return (
    <div>
      <NotifyEmailField service="newsletter" label="Empfänger für Newsletter-Benachrichtigungen" />
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-center"><p className="text-2xl font-heading text-[#C5993A]">{confirmed}</p><p className="text-xs text-[#8899AA]">Bestätigt</p></div>
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-center"><p className="text-2xl font-heading text-yellow-400">{pending}</p><p className="text-xs text-[#8899AA]">Ausstehend</p></div>
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 text-center"><p className="text-2xl font-heading text-white">{topics.length}</p><p className="text-xs text-[#8899AA]">Themen</p></div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {[["subscribers", "Abonnenten", Users], ["topics", "Themen", Tag], ["send", "Senden", Send], ["campaigns", "Verlauf", Mail]].map(([key, label, Icon]) => (
          <Button key={key} onClick={() => setView(key)} variant={view === key ? "default" : "ghost"} className={view === key ? "bg-[#C5993A] text-white border-0 text-xs" : "text-[#8899AA] text-xs"} size="sm">
            <Icon className="w-3 h-3 mr-1" /> {label}
          </Button>
        ))}
      </div>

      {/* ====== SUBSCRIBERS ====== */}
      {view === "subscribers" && (
        <div className="space-y-2">
          {subscribers.length === 0 ? <p className="text-[#3A4F60] text-center py-8">Noch keine Abonnenten.</p> : subscribers.map(sub => (
            <div key={sub.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {sub.title && `${sub.title} `}{sub.first_name} {sub.last_name}
                  <span className={`ml-2 text-xs ${statusColor[sub.status]}`}>{statusLabel[sub.status]}</span>
                </p>
                <p className="text-[#8899AA] text-xs truncate">{sub.email}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {(sub.topics || []).map(t => <span key={t} className="text-[10px] bg-[#C5993A]/10 text-[#C5993A] px-2 py-0.5 rounded">{t}</span>)}
                  <span className="text-[10px] text-[#3A4F60]">{sub.salutation === "informal" ? "Du" : "Sie"}</span>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {sub.status !== "confirmed" && (
                  <Button onClick={() => resendConfirm(sub.id)} variant="ghost" size="sm" className="text-[#C5993A] hover:text-[#C5993A] text-xs" disabled={resending === sub.id} title="Bestätigungsmail erneut senden">
                    <RefreshCw className={`w-3 h-3 ${resending === sub.id ? "animate-spin" : ""}`} />
                  </Button>
                )}
                <Button onClick={() => openEdit(sub)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A]"><Pencil className="w-3 h-3" /></Button>
                <Button onClick={() => deleteSub(sub.id)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ====== TOPICS ====== */}
      {view === "topics" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#8899AA]">Themen, die bei der Newsletter-Anmeldung angezeigt werden</p>
            <Button onClick={openTopicCreate} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-xs" size="sm"><Plus className="w-3 h-3 mr-1" /> Neues Thema</Button>
          </div>
          <div className="space-y-2">
            {topics.map(topic => (
              <div key={topic.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{topic.label_de} <span className="text-[#8899AA] font-normal">/ {topic.label_en}</span></p>
                  <p className="text-[#3A4F60] text-xs">{topic.description_de}</p>
                  <p className="text-[10px] text-[#3A4F60] mt-1">Schlüssel: {topic.key_name} · Reihenfolge: {topic.sort_order}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button onClick={() => toggleTopicVisible(topic)} variant="ghost" size="sm" className={topic.visible ? "text-green-400" : "text-red-400"} title={topic.visible ? "Sichtbar" : "Ausgeblendet"}>
                    {topic.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  </Button>
                  <Button onClick={() => openTopicEdit(topic)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-[#C5993A]"><Pencil className="w-3 h-3" /></Button>
                  <Button onClick={() => deleteTopic(topic.id)} variant="ghost" size="sm" className="text-[#8899AA] hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ====== SEND ====== */}
      {view === "send" && (
        <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-6 space-y-4">
          <div><label className="block text-xs text-[#8899AA] mb-1">Betreff *</label><Input value={sendForm.subject} onChange={(e) => setSendForm({...sendForm, subject: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
          <div><label className="block text-xs text-[#8899AA] mb-1">Inhalt *</label><Textarea value={sendForm.body} onChange={(e) => setSendForm({...sendForm, body: e.target.value})} rows={8} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A] resize-none" placeholder="Newsletter-Text..." /></div>
          <div>
            <label className="block text-xs text-[#8899AA] mb-2">Zielgruppe *</label>
            <div className="flex gap-4 flex-wrap">
              {topics.map(topic => (
                <label key={topic.key_name} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={sendForm.topics.includes(topic.key_name)} onCheckedChange={() => toggleSendTopic(topic.key_name)} className="border-[#3A4F60] data-[state=checked]:bg-[#C5993A] data-[state=checked]:border-[#C5993A]" />
                  <span className="text-xs text-[#F0F4F8]">{topic.label_de}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#8899AA] mb-1">PDF-Anhang (optional)</label>
            <input type="file" accept=".pdf" onChange={(e) => setSendForm({...sendForm, attachment: e.target.files[0] || null})} className="text-xs text-[#8899AA] file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-[#C5993A]/20 file:text-[#C5993A]" />
            {sendForm.attachment && <p className="text-xs text-[#C5993A] mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {sendForm.attachment.name}</p>}
          </div>
          {sendResult && <p className="text-sm text-[#C5993A]">{sendResult}</p>}
          <Button onClick={handleSend} disabled={sending || !sendForm.subject || !sendForm.body || sendForm.topics.length === 0} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
            {sending ? "Wird versendet..." : `Newsletter an ${confirmed} Empfänger senden`}
          </Button>
        </div>
      )}

      {/* ====== CAMPAIGNS ====== */}
      {view === "campaigns" && (
        <div className="space-y-2">
          {campaigns.length === 0 ? <p className="text-[#3A4F60] text-center py-8">Noch keine Kampagnen.</p> : campaigns.map(c => (
            <div key={c.id} className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4">
              <p className="text-white text-sm font-medium">{c.subject}</p>
              <div className="flex gap-3 text-xs text-[#8899AA] mt-1">
                <span>{c.sent_count} Empfänger</span>
                {c.attachment_name && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{c.attachment_name}</span>}
                <span>{new Date(c.sent_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Subscriber Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-md" aria-describedby="edit-sub-desc">
          <DialogHeader><DialogTitle className="font-heading text-lg">Abonnent bearbeiten</DialogTitle><p id="edit-sub-desc" className="sr-only">Formular</p></DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <label className="block text-xs text-[#8899AA] mb-1">Anrede</label>
              <select value={editForm.salutation || "formal"} onChange={(e) => setEditForm({...editForm, salutation: e.target.value})} className="w-full h-9 rounded-md border border-[#3A4F60]/50 bg-[#0C1620] text-white px-3 text-sm">
                <option value="formal">Formell (Sie)</option><option value="informal">Persönlich (Du)</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="block text-xs text-[#8899AA] mb-1">Titel</label><Input value={editForm.title || ""} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Vorname</label><Input value={editForm.first_name || ""} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Nachname</label><Input value={editForm.last_name || ""} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
            </div>
            <div><label className="block text-xs text-[#8899AA] mb-1">E-Mail</label><Input value={editForm.email || ""} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
            <div>
              <label className="block text-xs text-[#8899AA] mb-1">Status</label>
              <select value={editForm.status || "confirmed"} onChange={(e) => setEditForm({...editForm, status: e.target.value})} className="w-full h-9 rounded-md border border-[#3A4F60]/50 bg-[#0C1620] text-white px-3 text-sm">
                <option value="confirmed">Bestätigt</option><option value="pending">Ausstehend</option><option value="unsubscribed">Abgemeldet</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)} className="text-[#8899AA] text-sm">Abbrechen</Button>
            <Button onClick={saveEdit} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Topic Create/Edit Dialog */}
      <Dialog open={!!topicDialog} onOpenChange={(o) => { if (!o) setTopicDialog(null); }}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-md" aria-describedby="topic-desc">
          <DialogHeader><DialogTitle className="font-heading text-lg">{topicDialog === "new" ? "Neues Thema" : "Thema bearbeiten"}</DialogTitle><p id="topic-desc" className="sr-only">Thema</p></DialogHeader>
          <div className="space-y-3 py-3">
            <div><label className="block text-xs text-[#8899AA] mb-1">Schlüssel (eindeutig) *</label><Input value={topicForm.key_name} onChange={(e) => setTopicForm({...topicForm, key_name: e.target.value})} placeholder="z.B. market_updates" className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" disabled={topicDialog !== "new"} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs text-[#8899AA] mb-1">Bezeichnung DE *</label><Input value={topicForm.label_de} onChange={(e) => setTopicForm({...topicForm, label_de: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Label EN *</label><Input value={topicForm.label_en} onChange={(e) => setTopicForm({...topicForm, label_en: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs text-[#8899AA] mb-1">Beschreibung DE</label><Input value={topicForm.description_de} onChange={(e) => setTopicForm({...topicForm, description_de: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
              <div><label className="block text-xs text-[#8899AA] mb-1">Description EN</label><Input value={topicForm.description_en} onChange={(e) => setTopicForm({...topicForm, description_en: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm" /></div>
            </div>
            <div><label className="block text-xs text-[#8899AA] mb-1">Reihenfolge</label><Input type="number" value={topicForm.sort_order} onChange={(e) => setTopicForm({...topicForm, sort_order: parseInt(e.target.value) || 0})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm w-24" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTopicDialog(null)} className="text-[#8899AA] text-sm">Abbrechen</Button>
            <Button onClick={saveTopic} disabled={!topicForm.key_name || !topicForm.label_de || !topicForm.label_en} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
